import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  Filter,
  TrendingUp,
  TrendingDown,
  Gift,
  Home,
  QrCode,
  User,
  History,
  ArrowUpRight,
  ArrowDownLeft,
  ChevronLeft,
  ChevronRight,
  RefreshCcw,
  Calendar,
  ShoppingBag,
  Gamepad2,
  Award,
  Zap
} from 'lucide-react';
import { getPointsHistory } from '../Redux/Actions/ClientActions';
import './MyPoints.css'; // Shared styles
import './PointsHistory.css'; // PointsHistory specific styles

const PointsHistory = () => {
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { client, points } = useSelector(state => state.clientReducer);
  const { history, loading } = useSelector(state => state.clientReducer);

  // Fix: Don't send 'all' as type to backend
  useEffect(() => {
    if (client?._id) {
      dispatch(getPointsHistory({
        clientId: client._id,
        page,
        type: filter === 'all' ? undefined : filter
      }));
    }
  }, [dispatch, client, page, filter]);

  // Helper to robustly date strings
  const parseDate = (dateString) => {
    if (!dateString) return new Date();
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? new Date() : date;
  };

  const formatTime = (dateString) => {
    const date = parseDate(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper for dynamic transaction styles
  const getTxStyle = (tx) => {
    const desc = (tx.description || "").toLowerCase();
    const isEarn = tx.points > 0;

    // EXPENSE / REDEMPTION
    if (!isEarn) return {
      icon: Gift,
      bgClass: 'bg-rose-500',
      textClass: 'text-rose-800',
      badgeBg: 'bg-rose-50',
      badgeText: 'text-rose-700',
      shadowClass: 'shadow-rose-500/20',
      amountClass: 'text-rose-600',
      label: 'Récompense'
    };

    // SPECIFIC EARN CATEGORIES
    if (desc.includes('jeu') || desc.includes('game')) return {
      icon: Gamepad2,
      bgClass: 'bg-purple-500',
      textClass: 'text-purple-800',
      badgeBg: 'bg-purple-50',
      badgeText: 'text-purple-700',
      shadowClass: 'shadow-purple-500/20',
      amountClass: 'text-emerald-600', // Points are still green (gain)
      label: 'Jeu'
    };

    if (desc.includes('bonus') || desc.includes('bienvenue')) return {
      icon: Zap,
      bgClass: 'bg-amber-500',
      textClass: 'text-amber-800',
      badgeBg: 'bg-amber-50',
      badgeText: 'text-amber-700',
      shadowClass: 'shadow-amber-500/20',
      amountClass: 'text-emerald-600',
      label: 'Bonus'
    };

    if (desc.includes('shop') || desc.includes('achat') || desc.includes('store')) return {
      icon: ShoppingBag,
      bgClass: 'bg-emerald-500',
      textClass: 'text-emerald-800',
      badgeBg: 'bg-emerald-50',
      badgeText: 'text-emerald-700',
      shadowClass: 'shadow-emerald-500/20',
      amountClass: 'text-emerald-600',
      label: 'Achat'
    };

    // DEFAULT EARN (GREEN)
    return {
      icon: ArrowDownLeft,
      bgClass: 'bg-emerald-500',
      textClass: 'text-emerald-800',
      badgeBg: 'bg-emerald-50',
      badgeText: 'text-emerald-700',
      shadowClass: 'shadow-emerald-500/20',
      amountClass: 'text-emerald-600',
      label: 'Gain'
    };
  };

  const isSameDay = (d1, d2) => {
    return d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();
  };

  // Group transactions by date
  const groupTransactions = (transactions) => {
    const groups = [];
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    transactions.forEach(tx => {
      const txDate = parseDate(tx.date || tx.createdAt);
      let label = txDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

      if (isSameDay(txDate, today)) {
        label = "Aujourd'hui";
      } else if (isSameDay(txDate, yesterday)) {
        label = "Hier";
      }

      // Capitalize first letter
      label = label.charAt(0).toUpperCase() + label.slice(1);

      const existingGroup = groups.find(g => g.label === label);
      if (existingGroup) {
        existingGroup.items.push(tx);
      } else {
        groups.push({ label, items: [tx] });
      }
    });
    return groups;
  };

  const groupedHistory = history ? groupTransactions(history) : [];

  const stats = {
    earned: history?.filter(t => t.points > 0).reduce((a, b) => a + b.points, 0) || 0,
    spent: history?.filter(t => t.points < 0).reduce((a, b) => a + Math.abs(b.points), 0) || 0
  };

  return (
    <div className="mobile-app-container bg-slate-50">
      {/* Decorative Blob */}
      <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-primary/10 to-transparent pointer-events-none z-0"></div>

      {/* Immersive Header */}
      <header className="mobile-native-header bg-transparent backdrop-blur-sm">
        <div className="header-main-content">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/my-points')} className="p-2 hover:bg-white/50 rounded-full transition-colors">
              <ArrowLeft size={20} />
            </button>
            <h1 className="header-title-premium">Historique</h1>
          </div>
          <div className="header-points-badge bg-white shadow-sm border border-slate-100">
            <span className="points-amount text-primary">{points?.toLocaleString() || 0}</span>
            <span className="text-[10px] ml-1 opacity-80 uppercase font-bold text-slate-500">pts</span>
          </div>
        </div>
      </header>

      <main className="mobile-main-feed animate-fade-in p-0 z-10 relative">

        {/* Sticky Stats & Filter Section */}
        <div className="sticky top-0 bg-slate-50/95 backdrop-blur-xl z-40 px-5 py-4 border-b border-slate-200/50 shadow-sm transition-all duration-300">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-4 rounded-2xl shadow-lg shadow-emerald-500/20 text-white relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:scale-125 transition-transform duration-500">
                <TrendingUp size={48} />
              </div>
              <span className="text-[10px] uppercase font-bold tracking-wider opacity-90">Total Gagnés</span>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-2xl font-bold tracking-tight">+{stats.earned}</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-rose-500 to-pink-600 p-4 rounded-2xl shadow-lg shadow-rose-500/20 text-white relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:scale-125 transition-transform duration-500">
                <TrendingDown size={48} />
              </div>
              <span className="text-[10px] uppercase font-bold tracking-wider opacity-90">Total Dépensés</span>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-2xl font-bold tracking-tight">-{stats.spent}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-2 p-1 bg-white rounded-full border border-slate-200 shadow-inner">
            {[
              { id: 'all', label: 'Tout' },
              { id: 'earned', label: 'Gains' },
              { id: 'spent', label: 'Dépenses' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`flex-1 py-2 px-3 rounded-full text-xs font-bold transition-all duration-300 ${filter === f.id
                  ? 'bg-primary text-white shadow-md scale-100'
                  : 'text-slate-500 hover:bg-slate-50'
                  }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Transactions List */}
        <div className="px-5 py-6 space-y-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
            </div>
          ) : groupedHistory.length > 0 ? (
            groupedHistory.map((group, groupIndex) => (
              <div key={groupIndex} className="animate-in" style={{ animationDelay: `${groupIndex * 0.1}s` }}>
                <div className="flex items-center gap-3 mb-4 pl-1">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 bg-white px-3 py-1 rounded-full border border-slate-100 shadow-sm">
                    <Calendar size={10} />
                    {group.label}
                  </h3>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
                </div>

                <div className="space-y-3">
                  {group.items.map((tx, i) => {
                    const style = getTxStyle(tx);
                    const TxIcon = style.icon;
                    return (
                      <div key={i} className="flex items-center justify-between bg-white p-3.5 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-300 group">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg ${style.shadowClass} ${style.bgClass} text-white group-hover:scale-110 transition-transform duration-300`}>
                            <TxIcon size={22} strokeWidth={2.5} />
                          </div>
                          <div>
                            <p className="font-bold text-sm text-slate-800 line-clamp-1">
                              {tx.description || style.label}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${style.badgeBg} ${style.badgeText} uppercase tracking-wide`}>
                                {style.label}
                              </span>
                              <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                                {formatTime(tx.date || tx.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="text-right">
                          <span className={`text-base font-extrabold block ${style.amountClass}`}>
                            {tx.points > 0 ? '+' : ''}{tx.points}
                          </span>
                          <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Points</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <History size={32} className="text-slate-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-700">Aucune activité</h3>
              <p className="text-sm text-slate-400 mt-1 px-8">
                Vos transactions de points apparaîtront ici.
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {history?.length > 9 && (
          <div className="flex justify-center gap-4 py-8">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center disabled:opacity-30 shadow-sm"
              disabled={page === 1}
            >
              <ChevronLeft size={20} className="text-slate-600" />
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center disabled:opacity-30 shadow-sm"
              disabled={history.length < 10}
            >
              <ChevronRight size={20} className="text-slate-600" />
            </button>
          </div>
        )}

        {/* Spacer for bottom nav */}
        <div className="h-20"></div>

      </main>

      {/* Shared Floating Bottom Nav */}
      <nav className="mobile-floating-nav">
        <button className="nav-btn" onClick={() => navigate('/my-points')}>
          <Home size={22} />
          <span>Accueil</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/rewards')}>
          <Gift size={22} />
          <span>Cadeaux</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/scan-points')}>
          <div className="nav-center-scan">
            <QrCode size={24} color="white" />
          </div>
        </button>
        <button className="nav-btn is-active">
          <div className="nav-active-dot"></div>
          <History size={22} />
          <span>Activités</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/profile')}>
          <User size={22} />
          <span>Profil</span>
        </button>
      </nav>
    </div>
  );
};

export default PointsHistory;